import {
  BoundDateField,
  BoundNumberField,
  BoundSelectField,
  BoundTextField,
  Button,
  Css,
  FormLines,
  ModalFooter,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useCallback } from "react";
import { useController } from "@rest-hooks/react";
import { Property, PropertySummary } from "src/routes/cma/endpoints";
import { ReadyPlan } from "src/routes/cma/endpoints/reports";
import { GenerateCompsEndpoint } from "src/routes/cma/endpoints/reports/GenerateCompsEndpoint";
import { propertyTypeOptions } from "src/utils/mappers";
import { Maybe } from "src/utils/types";
import { convertDateToString, parseMaybeStringDate } from "src/utils/dates";
import { CompFilter } from "../CompFilter";
import { SaveCompPropertyEndpoint } from "./SaveCompPropertyEndpoint";
import { CompFilterPolygonGeometry } from "src/routes/cma/steps/comparables/ComparablesStep";

interface AddCompPropertyDetailsFormProps {
  subjectProperty: Property;
  newComp: PropertySummary | undefined;
  isManualMode: boolean;
  compDpids: string[];
  versionId: string;
  dpid: string;
  readyPlan: ReadyPlan;
  compFilter: CompFilter;
  compFilterPolygon?: CompFilterPolygonGeometry | undefined;
}

export function AddCompPropertyDetailsForm({
  subjectProperty,
  newComp,
  isManualMode,
  compDpids,
  versionId,
  dpid,
  readyPlan,
  compFilter,
  compFilterPolygon,
}: AddCompPropertyDetailsFormProps) {
  const { closeModal } = useModal();
  const { fetch } = useController();
  const { triggerNotice } = useSnackbar();

  const isReadOnly = !!newComp && !isManualMode;

  const formState = useFormState({
    config: formConfig,
    init: {
      input: { newComp, subjectProperty, isManualMode },
      map: mapToForm,
    },
  });

  const handleSave = useCallback(async () => {
    const comp = formState.value;
    const compDpid = comp.dpid_of_neighbor;
    const compAddress = comp.full_street_address;

    if (compDpid && compDpids.includes(compDpid)) {
      triggerNotice({ icon: "info", message: `Comp ${compAddress} is already in the table` });
    } else {
      // Convert date to string format for API
      const compToSave: SaveCompPropertyDetailsApiInput = {
        ...comp,
        last_sold_date: comp.last_sold_date ? convertDateToString(comp.last_sold_date) : undefined,
      };

      // add new comp
      const response = await fetch(SaveCompPropertyEndpoint, {
        dpid,
        versionId,
        comp: compToSave,
        uwReadyPlanId: readyPlan.id,
      });

      if (response.Code === "InternalServerError") {
        triggerNotice({ icon: "error", message: `Comp ${compAddress} was unable to be added` });
      } else {
        // fetch to add comp to comp table
        await fetch(GenerateCompsEndpoint, {
          dpid,
          underwriting_report_ready_plan_id: readyPlan.id,
          filter: compFilter,
          polygonFeature: compFilterPolygon,
        });
        triggerNotice({ icon: "success", message: `Added comp ${compAddress}` });
      }
    }
    closeModal();
  }, [
    formState,
    compDpids,
    closeModal,
    fetch,
    triggerNotice,
    readyPlan,
    dpid,
    versionId,
    compFilter,
    compFilterPolygon,
  ]);

  function renderForm() {
    return (
      <>
        <FormLines>
          <div css={Css.dg.gtc("1fr 1fr").gap2.$}>
            <BoundTextField field={formState.full_street_address} readOnly={isReadOnly} />
            <BoundTextField field={formState.unit_number} label="Unit Number" readOnly={isReadOnly} />
            <BoundTextField field={formState.city_name} readOnly={isReadOnly} />
            <BoundTextField field={formState.state} readOnly={isReadOnly} />
            <BoundTextField field={formState.zip_code} readOnly={isReadOnly} />
            <BoundSelectField
              label="Property Type"
              field={formState.property_type_simple}
              options={propertyTypeOptions}
              getOptionLabel={(o) => o.name}
              getOptionValue={(o) => o.id}
              readOnly={isReadOnly}
            />
          </div>
          <div css={Css.mt2.pt2.bt.bcGray300.$}>
            <div css={Css.sm.fw6.mb2.$}>Property Details</div>
            <div css={Css.dg.gtc("1fr 1fr").gap2.$}>
              <BoundNumberField
                label="Last Sold Price"
                field={formState.last_sold_price}
                type="dollars"
                numFractionDigits={0}
                readOnly={isReadOnly}
              />
              <BoundDateField
                label="Last Sold Date"
                field={formState.last_sold_date}
                useYearPicker
                readOnly={isReadOnly}
              />
              <BoundNumberField
                label="Year Built or Renovated"
                field={formState.year_built_or_renovated}
                numFractionDigits={0}
                readOnly={isReadOnly}
              />
              <BoundNumberField
                label="Finished Sqft"
                field={formState.finished_sqft}
                numFractionDigits={0}
                readOnly={isReadOnly}
              />
              <BoundNumberField
                label="Above Ground Sqft"
                field={formState.above_ground_sqft}
                numFractionDigits={0}
                readOnly={isReadOnly}
              />
              <BoundNumberField
                label="Finished Basement Sqft"
                field={formState.finished_basement_sqft}
                numFractionDigits={0}
                readOnly={isReadOnly}
              />
              <BoundNumberField
                label="Unfinished Basement Sqft"
                field={formState.unfinished_basement_sqft}
                numFractionDigits={0}
                readOnly={isReadOnly}
              />
              <BoundNumberField
                label="Lot Size"
                field={formState.lot_size}
                numFractionDigits={0}
                readOnly={isReadOnly}
              />
              <BoundNumberField
                label="Number of Bedrooms"
                field={formState.num_bedrooms}
                numFractionDigits={0}
                readOnly={isReadOnly}
              />
              <BoundNumberField
                label="Number of Baths"
                field={formState.num_baths}
                numFractionDigits={1}
                readOnly={isReadOnly}
              />
              <BoundNumberField
                label="Number of Stories"
                field={formState.num_stories}
                numFractionDigits={1}
                readOnly={isReadOnly}
              />
              <BoundNumberField
                label="Garage Number of Cars"
                field={formState.garage_num_cars}
                numFractionDigits={0}
                readOnly={isReadOnly}
              />
            </div>
          </div>
        </FormLines>
        <ModalFooter>
          <Button label="Cancel" variant="tertiary" onClick={closeModal} />
          <Button label="Add" onClick={handleSave} disabled={!formState.valid} />
        </ModalFooter>
      </>
    );
  }

  return <Observer>{() => renderForm()}</Observer>;
}

export interface SaveCompPropertyDetailsInput {
  dpid_of_neighbor: Maybe<string>;
  full_street_address: Maybe<string>;
  city_name: Maybe<string>;
  state: Maybe<string>;
  zip_code: Maybe<string>;
  unit_number: Maybe<string>;
  property_type_simple: Maybe<string>;
  last_sold_price: Maybe<number>;
  last_sold_date: Maybe<Date>;
  year_built_or_renovated: Maybe<number>;
  finished_sqft: Maybe<number>;
  above_ground_sqft: Maybe<number>;
  finished_basement_sqft: Maybe<number>;
  unfinished_basement_sqft: Maybe<number>;
  lot_size: Maybe<number>;
  num_bedrooms: Maybe<number>;
  num_baths: Maybe<number>;
  num_stories: Maybe<number>;
  garage_num_cars: Maybe<number>;
  latitude: Maybe<number>;
  longitude: Maybe<number>;
}

// API request format (date as string)
export interface SaveCompPropertyDetailsApiInput extends Omit<SaveCompPropertyDetailsInput, "last_sold_date"> {
  last_sold_date: Maybe<string>;
}

export type FormInput = SaveCompPropertyDetailsInput;
export type FormState = ObjectState<FormInput>;

export const formConfig: ObjectConfig<FormInput> = {
  dpid_of_neighbor: { type: "value" },
  full_street_address: { type: "value", rules: [required] },
  city_name: { type: "value", rules: [required] },
  state: { type: "value", rules: [required] },
  zip_code: { type: "value", rules: [required] },
  unit_number: { type: "value" },
  property_type_simple: { type: "value" },
  last_sold_price: { type: "value" },
  last_sold_date: { type: "value" },
  year_built_or_renovated: { type: "value" },
  finished_sqft: { type: "value" },
  above_ground_sqft: { type: "value" },
  finished_basement_sqft: { type: "value" },
  unfinished_basement_sqft: { type: "value" },
  lot_size: { type: "value" },
  num_bedrooms: { type: "value" },
  num_baths: { type: "value" },
  num_stories: { type: "value" },
  garage_num_cars: { type: "value" },
  latitude: { type: "value" },
  longitude: { type: "value" },
};

interface MapToFormInput {
  subjectProperty: Property;
  newComp: PropertySummary | undefined;
  isManualMode: boolean;
}

export function mapToForm({ subjectProperty, newComp: p, isManualMode }: MapToFormInput): FormInput {
  // When a property is found in RDS, populate what we can from PropertySummary
  // When manually entering, start with empty values (except defaults from subject property for location)
  return {
    dpid_of_neighbor: p?.dpid,
    full_street_address: p?.full_street_address ?? undefined,
    city_name: p?.city_name ?? (isManualMode ? undefined : subjectProperty.city_name.toUpperCase()),
    state: p?.state ?? (isManualMode ? undefined : subjectProperty.state),
    zip_code: p?.zip_code ?? (isManualMode ? undefined : subjectProperty.zip_code),
    unit_number: p?.unit_number,
    property_type_simple: p?.property_type_simple,
    last_sold_price: undefined,
    last_sold_date: undefined, // Will be Date | null | undefined for BoundDateField
    year_built_or_renovated: undefined,
    finished_sqft: undefined,
    above_ground_sqft: undefined,
    finished_basement_sqft: undefined,
    unfinished_basement_sqft: undefined,
    lot_size: undefined,
    num_bedrooms: undefined,
    num_baths: undefined,
    num_stories: undefined,
    garage_num_cars: undefined,
    latitude: p?.latitude,
    longitude: p?.longitude,
  };
}
